"use client";

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

/**
 * The reviewer's pick list: a set of spec ids starred on the board, persisted
 * in localStorage so it survives reloads and palette switches. Exposed to the
 * SpecFrame (star toggle) and the index (picks drawer + copy-as-text).
 */

const STORAGE_KEY = "mlx-concept-picks";

type PicksValue = {
  picks: string[];
  isPicked: (id: string) => boolean;
  toggle: (id: string) => void;
  clear: () => void;
  ready: boolean;
};

const PicksContext = createContext<PicksValue | null>(null);

export function PicksProvider({ children }: { children: ReactNode }) {
  const [picks, setPicks] = useState<string[]>([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? (JSON.parse(raw) as unknown) : [];
      if (Array.isArray(parsed)) setPicks(parsed.filter((v) => typeof v === "string"));
    } catch {
      // Unreadable storage — start empty.
    }
    setReady(true);
  }, []);

  useEffect(() => {
    if (!ready) return;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(picks));
    } catch {
      // Storage may be unavailable; the in-memory list still works.
    }
  }, [picks, ready]);

  const toggle = useCallback((id: string) => {
    setPicks((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]));
  }, []);
  const clear = useCallback(() => setPicks([]), []);
  const isPicked = useCallback((id: string) => picks.includes(id), [picks]);

  const value = useMemo(
    () => ({ picks, isPicked, toggle, clear, ready }),
    [picks, isPicked, toggle, clear, ready],
  );
  return <PicksContext.Provider value={value}>{children}</PicksContext.Provider>;
}

/** Null outside a provider (e.g. the render surface), so frames can hide the star. */
export function usePicks(): PicksValue | null {
  return useContext(PicksContext);
}
